
import { useState, useEffect } from 'react'
import Fab from '@mui/material/Fab'
import AppBar from '@mui/material/AppBar'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import MuiToolbar from '@mui/material/Toolbar'
import Icon from 'src/@core/components/icon'
import themeConfig from 'src/configs/themeConfig'
import Customizer from 'src/@core/components/customizer'
import ScrollToTop from 'src/@core/components/scroll-to-top'
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { useAuth } from 'src/hooks/useAuth'

import Footer from './components/shared-components/footer'
import Navigation from './components/horizontal/navigation'
import AppBarContent from './components/horizontal/app-bar-content'

const HorizontalLayoutWrapper = styled('div')({
  minHeight: '100vh',
  display: 'flex'
})

const MainContentWrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  minWidth: 0,
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default
}))

const Toolbar = styled(MuiToolbar)(({ theme }) => ({
  width: '100%',
  padding: `${theme.spacing(0, 6)} !important`,
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(4)
  },
  [theme.breakpoints.down('xs')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}))

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  padding: theme.spacing(6),
  transition: 'padding .25s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4)
  }
}))

const HorizontalLayout = props => {

  const {
    hidden,
    children,
    settings,
    scrollToTop,
    footerProps,
    saveSettings,
    contentHeightFixed,
    horizontalLayoutProps
  } = props

  const { skin, appBar, navHidden, appBarBlur, contentWidth } = settings

  const auth = useAuth()
  const isCampaigner = auth.user?.role === 'campaigner'

  const [isScrolled, setIsScrolled] = useState(false)

  const appBarProps = horizontalLayoutProps?.appBar?.componentProps

  const userNavMenuContent = horizontalLayoutProps?.navMenu?.content

  let userAppBarStyle = {}

  if (appBarProps && appBarProps.sx) {
    userAppBarStyle = appBarProps.sx
  }
  const userAppBarProps = Object.assign({}, appBarProps)

  delete userAppBarProps.sx

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <HorizontalLayoutWrapper className='layout-wrapper'>
      <MainContentWrapper className='layout-content-wrapper'>
 
        <AppBar
          color='default'
          elevation={skin === 'bordered' ? 0 : 3}
          className='layout-navbar-and-nav-container'
          position={appBar === 'fixed' ? 'sticky' : 'static'}
          sx={{
            alignItems: 'center',
            color: 'text.primary',
            justifyContent: 'center',
            backgroundColor: 'background.paper',
            ...(appBar === 'static' && { zIndex: 13 }),
            ...(skin === 'bordered' && { borderBottom: theme => `1px solid ${theme.palette.divider}` }),
            transition: 'border-bottom 0.2s ease-in-out, backdrop-filter .25s ease-in-out, box-shadow .25s ease-in-out',
            ...(appBar === 'fixed'
              ? appBarBlur && {
                backdropFilter: 'blur(8px)',
                backgroundColor: theme => hexToRGBA(theme.palette.background.paper, 0.9)
              }
              : {}),
            ...userAppBarStyle
          }}
          {...userAppBarProps}
        >
         
          <Box
            className='layout-navbar'
            sx={{
              background: `linear-gradient(120deg, #ffffff 0%, #ffffff calc(100% - 450px), transparent calc(100% - 649px)) no-repeat,
                            linear-gradient(120deg, #ECF0F1 0%, #ECF0F1 calc(100% - 235px), transparent calc(100% - 299px)) no-repeat,
                          #1B205C`,
              backgroundSize: '100% 100%',
              backgroundPosition: 'right top',
              '@media (max-width: 1400px)': {
                background: `linear-gradient(120deg, #ffffff 0%, #ffffff calc(100% - 410px), transparent calc(100% - 649px)) no-repeat,
                            linear-gradient(120deg, #ECF0F1 0%, #ECF0F1 calc(100% - 235px), transparent calc(100% - 299px)) no-repeat,
                          #1B205C`,
              },
              '@media (max-width: 1200px)': {
                background: `linear-gradient(120deg, #ffffff 0%, #ffffff calc(100% - 440px), transparent calc(100% - 399px)) no-repeat,
                              linear-gradient(120deg, #ECF0F1 0%, #ECF0F1 calc(100% - 450px), transparent calc(100% - 349px)) no-repeat,
                            #1B205C`
              },
              '@media (max-width: 768px)': {
                background: `linear-gradient(120deg, #ffffff 0%, #ffffff calc(100% - 240px), transparent calc(100% - 199px)) no-repeat,
                              linear-gradient(120deg, #ECF0F1 0%, #ECF0F1 calc(100% - 250px), transparent calc(100% - 149px)) no-repeat,
                            #1B205C`
              },

              width: '100%',
              ...(navHidden ? {} : { borderBottom: theme => `1px solid ${theme.palette.divider}` })
            }}
          >
            <Toolbar
              className='navbar-content-container'
              sx={{
                mx: 'auto',
                ...(contentWidth === 'boxed'),
                minHeight: isScrolled ? '36px !important' : '52px !important',
                transition: 'min-height 0.3s ease'
              }}
            >
              <AppBarContent
                {...props}
                hidden={hidden}
                settings={settings}
                saveSettings={saveSettings}
                appBarContent={horizontalLayoutProps?.appBar?.content}
                appBarBranding={horizontalLayoutProps?.appBar?.branding}
                isScrolled={isScrolled}
              />
            </Toolbar>
          </Box>

          {/* Navigation Menu */}
          {navHidden ? null : (
            <Box className='layout-horizontal-nav' sx={{
              width: '100%',
              marginTop: '-1px',
              ...horizontalLayoutProps?.navMenu?.sx,
              background: `
        linear-gradient(
          -60deg,
          #1B205C 0%,
          #1B205C 70%,
          #ECF0F1 70%,
          #ECF0F1 75%,
          #b4b8d5  100%,
          #ffffff 100%
        )
      `,
              '@media (max-width: 1900px)': {
                background: `
          linear-gradient(
            -60deg,
           #1B205C 0%,
          #1B205C 68%,
          #ECF0F1 70%,
          #ECF0F1 75%,
          #b4b8d5  100%,
          #ffffff 100%
          )
        `,
              },
              '@media (max-width: 1800px)': {
                background: `
          linear-gradient(
            -60deg,
           #1B205C 0%,
          #1B205C 72%,
          #ECF0F1 70%,
          #ECF0F1 75%,
          #b4b8d5  100%,
          #ffffff 100%
          )
        `,
              },
              '@media (max-width: 1700px)': {
                background: `
          linear-gradient(
            -60deg,
           #1B205C 0%,
          #1B205C 75%,
          #ECF0F1 70%,
          #ECF0F1 75%,
          #b4b8d5  100%,
          #ffffff 100%
          )
        `,
              },
              '@media (max-width: 1600px)': {
                background: `
          linear-gradient(
            -60deg,
           #1B205C 0%,
          #1B205C 82%,
          #ECF0F1 70%,
          #ECF0F1 75%,
          #b4b8d5  100%,
          #ffffff 100%
          )
        `,
              },
              '@media (max-width: 1500px)': {
                background: `
          linear-gradient(
            -60deg,
           #1B205C 0%,
          #1B205C 87%,
          #ECF0F1 70%,
          #ECF0F1 75%,
          #b4b8d5  100%,
          #ffffff 100%
          )
        `,
              },
              '@media (max-width: 1400px)': {
                background: `
          linear-gradient(
            -60deg,
           #1B205C 0%,
          #1B205C 77%,
          #ECF0F1 70%,
          #ECF0F1 75%,
          #b4b8d5  100%,
          #ffffff 100%
          )
        `,
              },
              '@media (max-width: 1300px)': {
                background: `
          linear-gradient(
            -60deg,
           #1B205C 0%,
          #1B205C 82%,
          #ECF0F1 70%,
          #ECF0F1 75%,
          #b4b8d5  100%,
          #ffffff 100%
          )
        `,
              },
              '@media (max-width: 768px)': {
                background: `
          linear-gradient(
            -60deg,
            #1B205C 0%,
          #1B205C 60%,
          #ECF0F1 70%,
          #ECF0F1 75%,
          #b4b8d5  100%,
          #ffffff 100%
          )
        `,
              },
            }}>
              <Toolbar
                className='horizontal-nav-content-container'
                sx={{
                  justifyContent: 'right',
                  paddingRight: isCampaigner ? '440px !important' : '350px !important',
                  '@media (max-width: 2000px)': {
                    paddingRight: isCampaigner ? '400px !important' : '310px !important'
                  },
                  '@media (max-width: 1400px)': {
                    paddingRight: isCampaigner ? '140px !important' : '50px !important'
                  },
                  '@media (max-width: 1200px)': {
                    paddingRight: isCampaigner ? '190px !important' : '100px !important'
                  },
                  '@media (max-width: 768px)': {
                    paddingRight: isCampaigner ? '140px !important' : '50px !important'
                  },
                  '@media (max-width: 480px)': {
                    paddingRight: isCampaigner ? '105px !important' : '15px !important'
                  },
                  
                  ...(contentWidth === 'boxed'
                 

                  ),
                  minHeight: isScrolled ? '32px !important' : '46px !important',
                  transition: 'min-height 0.3s ease'
                }}
              >
                {(userNavMenuContent && userNavMenuContent(props)) || (
                  <>
                    <Navigation
                      {...props}
                     
                      horizontalNavItems={
                        auth.user && auth.user.role === 'campaigner'
                          ? horizontalLayoutProps.navMenu?.navItems.filter(item => item.title !== 'Administration')
                          : horizontalLayoutProps.navMenu?.navItems
                      }
                    />
                  </>
                )}
              </Toolbar>
            </Box>
          )}
        </AppBar>

        <ContentWrapper
          className='layout-page-content'
          sx={{
            ...(contentHeightFixed && { display: 'flex', overflow: 'hidden' }),
            ...(contentWidth === 'boxed'
            )
          }}
        >
          {children}
        </ContentWrapper>

        <Footer {...props} footerStyles={footerProps?.sx} footerContent={footerProps?.content} />

        {themeConfig.disableCustomizer || hidden ? null : <Customizer />}

        {scrollToTop ? (
          scrollToTop(props)
        ) : (
          <ScrollToTop className='mui-fixed'>
            <Fab color='primary' size='small' aria-label='scroll back to top'>
              <Icon icon='mdi:arrow-up' />
            </Fab>
          </ScrollToTop>
        )}
      </MainContentWrapper>
    </HorizontalLayoutWrapper>
  )
}

export default HorizontalLayout
